"use client";

/**
 * §12.8 SCROLL RESTORATION (12-Motion.dc.html):
 *
 * "The list's scrollTop is captured on expand and restored exactly on
 * collapse, a stored number applied synchronously in a layout effect,
 * never scrollIntoView. Route-level scroll restoration is manual, not the
 * browser default, because the library is virtualised and the browser
 * restores against the wrong content height. Returning from a sound page
 * to the library lands on the row you left, every time."
 *
 * DECISION on load-more page count (the second half of the "restoring
 * position may require restoring the loaded page count too" question in
 * the brief): /sounds already mirrors the highest-loaded "Load N more"
 * batch into `?page=N` (sounds-page-client.tsx's handlePageChange), and on
 * mount the page walks every batch from 1 up to that target forward before
 * settling (the `isPriming` state machine). Browser back/forward restores
 * the URL, including `?page=N`, from history automatically — so the page
 * count is already restored by the existing URL-sync mechanism with no
 * separate code path needed here. This hook owns exactly one number: the
 * scrollY offset within that reproduced document.
 *
 * `ready` must stay false until every batch implied by the current URL has
 * finished loading (no fetch/priming in flight) — restoring against a
 * shorter document than what was scrolled to would clamp to the bottom,
 * the exact "wrong content height" failure the spec calls out for a
 * virtualised list; the same problem applies to an incrementally-loaded
 * one.
 */

import { useEffect, useLayoutEffect, useRef } from "react";

const KEY_PREFIX = "tls-scroll:";

/**
 * The URL the library is about to push IN PLACE (see markInPlaceNavigation).
 * One slot, not a per-URL flag: at most one such push is ever pending.
 */
const IN_PLACE_KEY = `${KEY_PREFIX}__in-place`;

function storageKey(pathname: string, search: string): string {
  return `${KEY_PREFIX}${pathname}${search}`;
}

function currentUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function safeReadRaw(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(key: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage disabled — nothing to clear.
  }
}

/**
 * Declare that the library is about to push `url` without leaving the list:
 * "Load N more" mirrors the highest loaded batch into `?page=N` (see
 * sounds-page-client.tsx handlePageChange). That push is a URL change, not
 * a navigation — the visitor is still looking at the rows they were looking
 * at — so restoration must NOT run against it. Without this, a (re)mount
 * following the push reads the offset stored for `?page=N` on some EARLIER
 * visit and applies it, and the page jumps out from under the visitor.
 *
 * Call it immediately before the push. The mark is consumed by the first
 * restoration attempt or the first scroll snapshot under that URL, whichever
 * comes first, so it never outlives the push it describes.
 */
export function markInPlaceNavigation(url: string): void {
  safeWriteRaw(IN_PLACE_KEY, url);
}

function safeWriteRaw(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Quota exceeded / disabled storage — silent, matches announce.ts posture.
  }
}

function safeRead(key: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const y = Number(raw);
    return Number.isFinite(y) && y > 0 ? y : null;
  } catch {
    // Private browsing / storage disabled — degrade to no restoration.
    return null;
  }
}

function safeWrite(key: string, y: number): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, String(y));
  } catch {
    // Quota exceeded / disabled storage — silent, matches announce.ts posture.
  }
}

/**
 * Route-level manual scroll restoration for /sounds. Call with `ready` set
 * to true only once the page count implied by the current URL has finished
 * loading (e.g. `!isLoading && !isPriming`). Persists the scroll offset
 * continuously (rAF-throttled) so a route change away always has a fresh
 * value, and restores it exactly once, synchronously before paint, the
 * first time `ready` becomes true after mount.
 */
export function useSoundsScrollRestoration(ready: boolean): void {
  const restoredRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame: number | null = null;
    const write = () => {
      frame = null;
      const url = currentUrl();
      // This instance survived the in-place push and is scrolling under the
      // new URL: the mark has done its job (nothing remounted), so clear it
      // before it can suppress a genuine restoration on a later reload.
      if (safeReadRaw(IN_PLACE_KEY) === url) safeRemove(IN_PLACE_KEY);
      safeWrite(storageKey(window.location.pathname, window.location.search), window.scrollY);
    };
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(write);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
      write(); // Final snapshot at unmount — the route-change-away moment.
    };
  }, []);

  useLayoutEffect(() => {
    if (!ready || restoredRef.current || typeof window === "undefined") return;
    restoredRef.current = true;
    // A mount that follows the library's own `?page=` push is not an
    // arrival: leave the visitor exactly where they are.
    if (safeReadRaw(IN_PLACE_KEY) === currentUrl()) {
      safeRemove(IN_PLACE_KEY);
      return;
    }
    const y = safeRead(storageKey(window.location.pathname, window.location.search));
    if (y === null) return;
    // A stored number applied synchronously, never scrollIntoView (§12.8).
    window.scrollTo(0, y);
  }, [ready]);
}
